import { buildApiUrl, sendGetRequest, Type } from '@/api/api'
import { assertSchemaAndReturn } from '@/helpers/assert-schema'
import { z } from 'zod'
import { ListedTariffSchema, TariffsSchema, UtilityProvidersSchema } from './schemas'
import { validateResponse } from '@/shared/api/validation'
import { createTariffManagementClient } from '@/shared/api/client'

const client = createTariffManagementClient()

function isAdditionalCharge(chargeId: number) {
  return chargeId > 6;
}

export async function getUtilities(countryId: number|undefined = undefined) {
  const url = buildApiUrl(Type.TariffManagement, 'providers')

  if (countryId) {
    url.searchParams.set('countryId', countryId.toString())
  }

  const { data } = await sendGetRequest(url)
  return assertSchemaAndReturn(
    data,
    UtilityProvidersSchema
  ).sort((a, b) => a.Name.localeCompare(b.Name))
}

export const listTariffsByUtilityId = (utilityId: number) => validateResponse(
  client.sendGet(`tariffs/names`, { providerIds: utilityId }),
  z.object({ Data: z.array(ListedTariffSchema) })
).then(
  x => x.Data
)

export async function getTariffById(tariffId: number) {
  const { data } = await sendGetRequest(buildApiUrl(Type.TariffManagement, `tariffs?TariffStructureIds=${tariffId}`))

  const tariffs = assertSchemaAndReturn(
    data,
    TariffsSchema
  )

  if (tariffs.length < 1) {
    return undefined
  }

  const tariff = tariffs[0]

  return {
    id: tariff.Id,
    name: tariff.Name + ' (' + tariff.Source + ')' + ' - ' + tariff.Periods[0].YearOfTariff,
    isTimeOfUse: tariff.IsTimeOfUse,
    additionalCharges: tariff.Periods
      .filter(x => isAdditionalCharge(x.ChargeNameId))
      .map(
        period => ({
          id: period.Id,
          name: period.ChargeName,
          rateInCents: period.RateInCents,
          seasonId: period.SeasonId,
          seasonName: period.Season,
          year: period.YearOfTariff,
        })
      ),
    bands: tariff.Periods
      .filter(x => !isAdditionalCharge(x.ChargeNameId))
      .map(
        period => ({
          id: period.Id,
          name: tariff.IsTimeOfUse
            ? period.TimeOfUse ?? 'Unknown'
            : period.ChargeName,
          seasonId: period.SeasonId,
          seasonName: period.Season,
          rangeStart: period.StartOfPeriod,
          rangeEnd: period.EndOfPeriod,
          rateInCents: period.RateInCents,
          year: period.YearOfTariff,
        })
      )
  }
}
